import { lazy, type ComponentType } from 'react';

const RELOAD_FLAG = 'chunk-reload-attempted';

/**
 * React.lazy that survives a deployment.
 *
 * Vite gives every chunk a content hash. After a deploy the old files are gone, but a tab
 * that was already open still holds the previous index.html — so the first navigation to a
 * not-yet-loaded page asks for a chunk that now returns 404 and React Router shows
 * "Failed to fetch dynamically imported module". That is exactly the moment a waiter taps
 * through the app during service, or the wall tablet has been sitting open for days.
 *
 * Reloading once picks up the new index.html. The session flag makes sure a genuinely
 * broken chunk cannot put the app into a reload loop: the second failure is passed on.
 */
export function lazyWithReload<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>
) {
  return lazy(async () => {
    try {
      const module = await factory();
      try {
        sessionStorage.removeItem(RELOAD_FLAG);
      } catch {}
      return module;
    } catch (error) {
      let alreadyTried = true; // if storage is unavailable, never reload
      try {
        alreadyTried = sessionStorage.getItem(RELOAD_FLAG) === '1';
        if (!alreadyTried) sessionStorage.setItem(RELOAD_FLAG, '1');
      } catch {}

      if (alreadyTried) throw error;

      console.warn('[chunk] Stale build detected, reloading once:', error);
      window.location.reload();
      // The page is going away; never resolve, so no error surfaces in the meantime.
      return new Promise<{ default: T }>(() => {});
    }
  });
}

export default lazyWithReload;
